import { formatDate } from "@sembark-travel/datetime-utils"
import {
	Alert,
	Badge,
	Box,
	Button,
	CheckboxInput,
	Col,
	Container,
	Divider,
	Dropdown,
	Grid,
	Heading,
	HorizontalScrollShadow,
	Icons,
	Inline,
	joinAttributes,
	Money,
	Select,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
	Time,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	DatePickerField,
	DayAndHourInputField,
	DurationInputField,
	ErrorMessage,
	FieldArray,
	Form,
	GetFieldValue,
	MarkdownInputField,
	SelectField,
	SubmissionError,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Breadcrumbs, ButtonLink, NavLink } from "@sembark-travel/ui-router"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import type { FormApi } from "final-form"
import React, { useEffect, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import { SelectTenantCurrencyInputField } from "../Currencies"
import { DocGroupXHR, DocumentFieldInput, IDocGroup } from "../DocGroups"
import { IDocType } from "../DocTypes"
import { generatePath } from "../router-utils"
import { SelectTripDestination } from "../TripDestinations"
import {
	TYPE_OF_VISA_OPTIONS,
	ENTRY_TYPE_OPTIONS,
	PURPOSE_OF_VISA_OPTIONS,
	TVisaServiceForm,
	TVisa,
	validateVisaForm,
	XHR,
	getTypeOfVisaDisplayName,
	getPurposeOfVisaDisplayName,
	getEntryTypeDisplayName,
	getTypeOfVisaOption,
	getEntryTypeOption,
	getPurposeOfVisaOption,
	getProcessingTimeInHours,
	TVisaTypeForm,
	validateVisaTypeForm,
} from "./store"
import { VisaServicesList } from "./List"
import { SelectWorldCountries } from "../World"
import { Markdown } from "@sembark-travel/ui-markdown"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { formatMoneyByDecimal } from "@sembark-travel/money"
import {
	ForbidUnlessAuthorized,
	PERMISSIONS,
	useCheckPermissions,
} from "../Auth"
import { useDragDrop } from "../TripPlanRequests"
import * as Validator from "yup"

export function VisaServices() {
	return (
		<ForbidUnlessAuthorized anyPermissions={[PERMISSIONS.VIEW_VISA_SERVICES]}>
			<VisaServicesList />
		</ForbidUnlessAuthorized>
	)
}

export function EmptyState() {
	const { hasPermission } = useCheckPermissions()
	return (
		<Stack gap="4">
			<Box
				borderBottomWidth="1"
				display={{ md: "flex" }}
				justifyContent="between"
				alignItems="center"
				paddingX={{ xs: "4", xl: "8" }}
				paddingY="3"
			>
				<Heading as="h4" fontSize="lg">
					Visa
				</Heading>
			</Box>
			<Inline justifyContent="center" alignItems="center">
				<Stack justifyContent="center" alignItems="center" gap="3">
					<Icons.Info size="10" color="muted" />
					<Text fontSize="lg">
						Create visa services to manage visa applications and processing
						workflows.
					</Text>
					{hasPermission(PERMISSIONS.CREATE_VISA_SERVICES) ? (
						<ButtonLink to={generatePath("/visa/new")}>
							<Icons.Plus />
							Add Visa Service
						</ButtonLink>
					) : null}
				</Stack>
			</Inline>
		</Stack>
	)
}

export function VisaBasicDetails({
	visaServiceId,
	children,
}: Readonly<{
	visaServiceId: string
	children?: (props: {
		service: TVisa
		onSuccess: () => void
	}) => React.ReactNode
}>) {
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()
	const {
		data: service,
		error,
		mutate,
	} = useSWR<TVisa>(`/api/visa-services/${visaServiceId}`, () =>
		XHR(xhr).showVisaServices(visaServiceId, { include: "deleted_by" })
	)

	if (!service) {
		if (!error) {
			return <Spinner alignCenter padding="8" />
		}
		return (
			<Alert status="error">{error.message || "Something went wrong"}</Alert>
		)
	}

	const {
		id,
		name,
		countries,
		currency,
		trip_destinations,
		created_by,
		created_at,
		document_groups,
	} = service

	function onSuccess() {
		mutate()
	}

	return (
		<Box key={visaServiceId}>
			<Breadcrumbs
				title="Service Details"
				items={[[generatePath("/visa"), "Visa"]]}
				actions={
					<Inline gap="4">
						{hasPermission(PERMISSIONS.CREATE_VISA_SERVICES) ? (
							<ButtonLink
								to={generatePath("/visa/:visaServiceId/edit", {
									visaServiceId: String(id),
								})}
								size="sm"
								state={{ service }}
							>
								<Icons.Pencil /> Edit
							</ButtonLink>
						) : null}

						<Dropdown alignRight>
							<Dropdown.ToggleButton size="sm" level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{hasPermission(PERMISSIONS.CREATE_VISA_SERVICES) ? (
									<DisableVisaServiceDialog
										service={service}
										onSuccess={onSuccess}
									>
										{({ open }) => (
											<Dropdown.MenuItem
												onClick={() => {
													open()
												}}
											>
												<Icons.Ban /> Disable
											</Dropdown.MenuItem>
										)}
									</DisableVisaServiceDialog>
								) : null}
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				}
			/>
			<Inline gap="4" paddingX="8" paddingY="6" bgColor="default">
				<Box>
					<Box padding="6" bgColor="primary" rounded="md">
						<Icons.MapPinned size="12" color="primary" />
					</Box>
				</Box>
				<Stack gap="4" width="full">
					<Text fontWeight="semibold" fontSize="xl">
						{name}
					</Text>
					<Inline gap="4" flexWrap="wrap">
						{countries ? (
							<Stack gap="1">
								<Inline alignItems="center" gap="1" color="muted">
									<Icons.Globe />
									<Text fontWeight="semibold">Country</Text>
								</Inline>
								<Text
									color="default"
									fontSize="base"
									maxWidth="sm"
									whiteSpace="normal"
									wordBreak="words"
									style={{ overflowWrap: "break-word" }}
								>
									{countries.map((c) => c.name).join(", ")}
								</Text>
							</Stack>
						) : null}
						{trip_destinations ? (
							<Inline gap="2">
								<Box borderLeftColor="muted" borderWidth="1" />
								<Stack gap="1">
									<Inline alignItems="center" gap="1" color="muted">
										<Icons.LocateFixed />
										<Text fontWeight="semibold" wordBreak="all">
											Destination
										</Text>
									</Inline>
									<Text
										color="default"
										fontSize="base"
										maxWidth="sm"
										whiteSpace="normal"
										wordBreak="words"
										style={{ overflowWrap: "break-word" }}
									>
										{trip_destinations.map((d) => d.name).join(", ")}
									</Text>
								</Stack>
							</Inline>
						) : null}
						{currency ? (
							<Inline gap="2">
								<Box borderLeftColor="muted" borderWidth="1" />
								<Stack gap="1">
									<Inline alignItems="center" gap="1" color="muted">
										<Icons.BankNotes />
										<Text fontWeight="semibold" wordBreak="all">
											Currency
										</Text>
									</Inline>
									<Text color="default" fontSize="base">
										{currency}
									</Text>
								</Stack>
							</Inline>
						) : null}
					</Inline>
					{document_groups && document_groups.length > 0 ? (
						<SelectedDocumentGroup documentGroup={document_groups[0]} />
					) : null}
					{created_by?.name && created_at ? (
						<Inline gap="1" alignItems="center">
							<Text color="default" as="span">
								<Icons.User /> Created By {created_by.name} on{" "}
								{formatDate(created_at, "DD MMM, YYYY")}
							</Text>
						</Inline>
					) : null}
				</Stack>
			</Inline>
			<Box borderTopWidth="1">
				<Tabs layout="col">
					<TabsList>
						<NavLink
							to={generatePath("/visa/:visaServiceId", {
								visaServiceId: String(visaServiceId),
							})}
							end
						>
							Visa Types
						</NavLink>
						<NavLink
							to={generatePath("/visa/:visaServiceId/activities", {
								visaServiceId: String(visaServiceId),
							})}
						>
							Log
						</NavLink>
					</TabsList>
				</Tabs>
			</Box>
			<Box>
				{typeof children === "function"
					? children({ service, onSuccess })
					: children}
			</Box>
		</Box>
	)
}

function DisableVisaServiceDialog({
	service,
	onSuccess,
	children,
}: {
	service: TVisa
	onSuccess: () => void
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{children({ open: open })}
			<Dialog open={isOpen} onClose={close} sm title="Disable Visa Service">
				<Form<{ service_id: number }>
					initialValues={{ service_id: service.id }}
					onSubmit={withServerErrors(async (values) => {
						const resp = await XHR(xhr).disableVisaService(values.service_id)
						showSnackbar(resp.message)
						onSuccess()
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<Text>
									This Visa Service will be removed from all visa service
									listing and selection pages. Existing usage of this Visa
									service will NOT be affected.
								</Text>
								<Divider sm />
								<Text color="warning">
									Are you sure you want to disable this Visa Services ?
								</Text>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting} status="warning">
									{submitting ? "Disabling..." : "I understand, disabled it."}
								</Button>
								<Button onClick={close} disabled={submitting}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

export function VisaServiceTypes({
	service,
	onSuccess,
}: {
	service: TVisa
	onSuccess: () => void
}) {
	const visaTypes = useMemo(
		() => service.visa_types || [],
		[service.visa_types]
	)
	const [showDeleted, setShowDeleted] = useState(false)
	const [nonDeletedServices, deletedServices] = useMemo(
		() =>
			(visaTypes || []).reduce<
				[Array<TVisa["visa_types"][number]>, Array<TVisa["visa_types"][number]>]
			>(
				([nonDeleted, deleted], s) => {
					if (s.deleted_at) {
						deleted.push(s)
					} else {
						nonDeleted.push(s)
					}
					return [nonDeleted, deleted]
				},
				[[], []]
			),
		[visaTypes]
	)

	const disableServicesCount = deletedServices.length
	const activeServicesCount = nonDeletedServices.length

	useEffect(() => {
		if (disableServicesCount === 0 && activeServicesCount === 0) return
		if (disableServicesCount === 0 && showDeleted) {
			setShowDeleted(false)
		}
		if (activeServicesCount === 0 && !showDeleted) {
			setShowDeleted(true)
		}
	}, [disableServicesCount, showDeleted, activeServicesCount])

	const visibleServices = showDeleted ? deletedServices : nonDeletedServices

	return (
		<Container bgColor={"inset"}>
			<Stack paddingY="8" gap="4">
				<Inline justifyContent="between" gap="2">
					<Heading as="h4">Visa Types</Heading>
					<Inline gap="2">
						{showDeleted ? null : (
							<ReArrangeVisaTypesDialog service={service} onSuccess={onSuccess}>
								{({ open }) => (
									<Button onClick={open}>
										<Icons.ArrowUpDown />
										Re-arrange
									</Button>
								)}
							</ReArrangeVisaTypesDialog>
						)}
						{visaTypes.length > 0 &&
						activeServicesCount &&
						disableServicesCount ? (
							<Button
								level="primary"
								status={showDeleted ? "primary" : "warning"}
								inline
								onClick={() => {
									setShowDeleted(!showDeleted)
								}}
							>
								<Inline gap="4" as="label" paddingY="2" paddingX="4">
									{showDeleted
										? `Only Enable (${activeServicesCount})`
										: `Only Disabled (${disableServicesCount})`}
								</Inline>
							</Button>
						) : null}
					</Inline>
				</Inline>
				<Stack as="ul" gap="4">
					{visibleServices?.map((visa_type) => (
						<Box
							as="li"
							key={visa_type.id}
							borderWidth="1"
							bgColor={visa_type.deleted_at ? "warning" : "default"}
							borderColor={visa_type.deleted_at ? "warning" : "default"}
							padding="4"
							rounded="md"
						>
							<VisaTypeService visaType={visa_type} onSuccess={onSuccess} />
						</Box>
					))}
				</Stack>
			</Stack>
		</Container>
	)
}

function VisaTypeService({
	visaType,
	onSuccess,
}: {
	visaType: TVisa["visa_types"][number]
	onSuccess: () => void
}) {
	const { hasPermission } = useCheckPermissions()
	const {
		name,
		duration_formatted,
		cost_price,
		selling_price,
		type_of_visa,
		purpose,
		apply_before_days,
		price_valid_till,
		entry_type,
		description,
		document_groups,
		created_at,
		created_by,
		updated_at,
		updated_by,
		deleted_at,
		deleted_by,
	} = visaType

	return (
		<Stack gap="4" paddingX="2">
			<Stack gap="2">
				<Inline gap="4" justifyContent="between">
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<Text fontWeight="semibold" fontSize="lg">
							{name}
						</Text>
						<Inline gap="2">
							<Badge primary>{getTypeOfVisaDisplayName(type_of_visa)}</Badge>
							<Badge accent>{getPurposeOfVisaDisplayName(purpose)}</Badge>
						</Inline>
					</Inline>
					{hasPermission(PERMISSIONS.CREATE_VISA_SERVICES) ? (
						<VisaTypeActions visaType={visaType} onSuccess={onSuccess} />
					) : null}
				</Inline>
				<Inline flexWrap="wrap">
					{joinAttributes(
						apply_before_days ? (
							<Box>
								<Text>
									<Icons.Clock color="muted" /> Processing Day or Hour:{" "}
									{apply_before_days} Days
								</Text>
							</Box>
						) : null,
						price_valid_till ? (
							<Box>
								<Text>
									<Icons.Calendar color="muted" /> Rate Valid Upto:{" "}
									{formatDate(price_valid_till, "DD MMM, YYYY")}
								</Text>{" "}
							</Box>
						) : null
					)}
				</Inline>
			</Stack>

			<Inline gap="4" flexWrap="wrap">
				{duration_formatted ? (
					<InfoStack
						icon={<Icons.Calendar color="muted" />}
						label="Duration"
						subtext={duration_formatted}
					/>
				) : null}
				{entry_type ? (
					<InfoStack
						icon={<Icons.ArrowUpDown color="muted" />}
						label="Entries"
						subtext={getEntryTypeDisplayName(entry_type)}
					/>
				) : null}
				{cost_price ? (
					<InfoStack
						icon={<Icons.BankNotes color="muted" />}
						label="Cost Price/Person"
						subtext={<Money money={cost_price} showCurrency />}
					/>
				) : null}
				{selling_price ? (
					<InfoStack
						icon={<Icons.BankNotes color="muted" />}
						label="Selling Price/Person"
						subtext={<Money money={selling_price} showCurrency />}
					/>
				) : null}
				{apply_before_days ? (
					<InfoStack
						icon={<Icons.CheckList color="muted" />}
						label="Application Minimum Days"
						subtext={`${apply_before_days} Days`}
					/>
				) : null}
			</Inline>
			<Divider marginY="0" />
			<DocumentInfo documentGroups={document_groups} />
			<Markdown>{description}</Markdown>
			<Divider marginY="0" />
			<Inline color="muted">
				{joinAttributes(
					created_at && created_by ? (
						<Inline gap="2" alignItems="baseline">
							<Box>
								<Icons.User />
							</Box>
							<Text fontSize="sm">
								Created by {created_by?.name} on <Time timestamp={created_at} />
							</Text>
						</Inline>
					) : null,
					updated_at && updated_by ? (
						<Inline gap="2" alignItems="baseline">
							<Box>
								<Icons.User />
							</Box>
							<Text fontSize="sm">
								Last updated by {updated_by?.name} on{" "}
								<Time timestamp={updated_at} />
							</Text>
						</Inline>
					) : null,
					deleted_at ? (
						<Inline gap="2" alignItems="baseline">
							<Box>
								<Icons.Ban />
							</Box>
							<Text fontSize="sm">
								Disabled {deleted_by ? `by ${deleted_by?.name}` : null} on{" "}
								<Time timestamp={deleted_at} />
							</Text>
						</Inline>
					) : null
				)}
			</Inline>
		</Stack>
	)
}

export function InfoStack({
	icon,
	label,
	subtext,
}: Readonly<{
	icon: React.JSX.Element
	label: string
	subtext: string | React.ReactNode
}>) {
	return (
		<Stack gap="1">
			<Inline fontSize="sm" gap="1">
				{icon}
				<Text color="muted">{label}</Text>
			</Inline>
			{typeof subtext === "string" ? (
				<Text fontSize="base">{subtext}</Text>
			) : (
				subtext
			)}
		</Stack>
	)
}

function VisaTypeActions({
	visaType,
	onSuccess,
}: {
	visaType: TVisa["visa_types"][number]
	onSuccess: () => void
}) {
	return (
		<Dropdown alignRight>
			<Dropdown.ToggleButton size="sm" level="tertiary">
				<Icons.DotsVertical />
			</Dropdown.ToggleButton>
			<Dropdown.Menu>
				{visaType.deleted_at ? null : (
					<VisaTypeEditDialog
						visaType={visaType}
						visaService={{} as TVisa}
						onSuccess={onSuccess}
					>
						{({ open }) => (
							<Dropdown.MenuItem onClick={open}>
								<Icons.Pencil /> Edit
							</Dropdown.MenuItem>
						)}
					</VisaTypeEditDialog>
				)}
				{visaType.deleted_at ? (
					<RestoreVisaTypeDialog visaType={visaType} onSuccess={onSuccess}>
						{({ open }) => (
							<Dropdown.MenuItem onClick={open}>
								<Icons.Refresh /> Restore
							</Dropdown.MenuItem>
						)}
					</RestoreVisaTypeDialog>
				) : (
					<DisableVisaTypeDialog visaType={visaType} onSuccess={onSuccess}>
						{({ open }) => (
							<Dropdown.MenuItem onClick={open}>
								<Icons.Ban /> Disable
							</Dropdown.MenuItem>
						)}
					</DisableVisaTypeDialog>
				)}
			</Dropdown.Menu>
		</Dropdown>
	)
}

function DisableVisaTypeDialog({
	visaType,
	children,
	onSuccess,
}: {
	visaType: TVisa["visa_types"][number]
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess: () => void
}) {
	const {
		name,
		apply_before_days,
		price_valid_till,
		entry_type,
		description,
		cost_price,
		selling_price,
		duration_formatted,
	} = visaType
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{children({
				open: () => {
					open()
				},
			})}
			<Dialog open={isOpen} onClose={close} md title="Disable Visa">
				<Form<{ type_id: number }>
					initialValues={{ type_id: visaType.id }}
					onSubmit={withServerErrors(async (values) => {
						const resp = await XHR(xhr).disableVisaType(values.type_id)
						showSnackbar(resp.message)
						onSuccess()
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body paddingBottom="6">
								<Stack gap="1">
									<Stack gap="1">
										<Text fontWeight="semibold" fontSize="md">
											Preview Details
										</Text>
										<Stack
											gap="2"
											padding="4"
											borderWidth="1"
											borderColor="muted"
											rounded="lg"
										>
											<Stack gap="2">
												<Inline gap="4" justifyContent="between">
													<Inline gap="2" alignItems="center">
														<Text fontWeight="semibold" fontSize="md">
															{name}
														</Text>
													</Inline>
												</Inline>

												<Inline>
													{joinAttributes(
														apply_before_days ? (
															<Inline gap="2" alignItems="center">
																<Icons.Clock />
																<Text>
																	{" "}
																	Processing Day or Hour: {
																		apply_before_days
																	}{" "}
																	Days
																</Text>
															</Inline>
														) : null,
														price_valid_till ? (
															<Inline gap="2" alignItems="center">
																<Icons.Calendar />
																<Text>
																	{" "}
																	Rate Valid Upto:{" "}
																	{formatDate(price_valid_till, "DD MMM, YYYY")}
																</Text>{" "}
															</Inline>
														) : null
													)}
												</Inline>
											</Stack>

											<Inline gap="4">
												{duration_formatted ? (
													<InfoStack
														icon={<Icons.Calendar color="muted" />}
														label="Duration"
														subtext={duration_formatted}
													/>
												) : null}
												{entry_type ? (
													<InfoStack
														icon={<Icons.ArrowUpDown color="muted" />}
														label="Entries"
														subtext={getEntryTypeDisplayName(entry_type)}
													/>
												) : null}
												{cost_price ? (
													<InfoStack
														icon={<Icons.BankNotes color="muted" />}
														label="Cost Price/Person"
														subtext={<Money money={cost_price} showCurrency />}
													/>
												) : null}
												{selling_price ? (
													<InfoStack
														icon={<Icons.BankNotes color="muted" />}
														label="Selling Price/Person"
														subtext={
															<Money money={selling_price} showCurrency />
														}
													/>
												) : null}
												{apply_before_days ? (
													<InfoStack
														icon={<Icons.CheckList color="muted" />}
														label="Application Minimum Days"
														subtext={`${apply_before_days} Days`}
													/>
												) : null}
											</Inline>
											<Markdown>{description}</Markdown>
										</Stack>
									</Stack>
									<Divider />
									<Alert
										status="warning"
										title="Are you sure you want to disable this Service ? Disabling the Service:"
									>
										<Box as="ul" listStyleType="disc" marginLeft="4">
											<li>Will disable this visa service</li>
											<li>
												Will hide the service from selection at the time of
												package creation
											</li>
										</Box>
									</Alert>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting} status="warning">
									{submitting ? "Disabling..." : "I understand, disabled it."}
								</Button>
								<Button onClick={close} disabled={submitting}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function RestoreVisaTypeDialog({
	visaType,
	children,
	onSuccess,
}: {
	visaType: TVisa["visa_types"][number]
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess: () => void
}) {
	const {
		name,
		apply_before_days,
		price_valid_till,
		entry_type,
		description,
		cost_price,
		selling_price,
		duration_formatted,
	} = visaType
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{children({
				open: () => {
					open()
				},
			})}
			<Dialog open={isOpen} onClose={close} md title="Restore Visa">
				<Form<{ type_id: number }>
					initialValues={{ type_id: visaType.id }}
					onSubmit={withServerErrors(async (values) => {
						const resp = await XHR(xhr).restoreVisaType(values.type_id)
						showSnackbar(resp.message)
						onSuccess()
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body paddingBottom="6">
								<Stack gap="1">
									<Stack gap="1">
										<Text fontWeight="semibold" fontSize="md">
											Preview Details
										</Text>
										<Stack
											gap="2"
											padding="4"
											borderWidth="1"
											borderColor="muted"
											rounded="lg"
										>
											<Stack gap="2">
												<Inline gap="4" justifyContent="between">
													<Inline gap="2" alignItems="center">
														<Text fontWeight="semibold" fontSize="md">
															{name}
														</Text>
													</Inline>
												</Inline>

												<Inline>
													{joinAttributes(
														apply_before_days ? (
															<Inline gap="2" alignItems="center">
																<Icons.Clock />
																<Text>
																	{" "}
																	Processing Day or Hour: {
																		apply_before_days
																	}{" "}
																	Days
																</Text>
															</Inline>
														) : null,
														price_valid_till ? (
															<Inline gap="2" alignItems="center">
																<Icons.Calendar />
																<Text>
																	{" "}
																	Rate Valid Upto:{" "}
																	{formatDate(price_valid_till, "DD MMM, YYYY")}
																</Text>{" "}
															</Inline>
														) : null
													)}
												</Inline>
											</Stack>

											<Inline gap="4">
												{duration_formatted ? (
													<InfoStack
														icon={<Icons.Calendar color="muted" />}
														label="Duration"
														subtext={duration_formatted}
													/>
												) : null}
												{entry_type ? (
													<InfoStack
														icon={<Icons.ArrowUpDown color="muted" />}
														label="Entries"
														subtext={getEntryTypeDisplayName(entry_type)}
													/>
												) : null}
												{cost_price ? (
													<InfoStack
														icon={<Icons.BankNotes color="muted" />}
														label="Cost Price/Person"
														subtext={<Money money={cost_price} showCurrency />}
													/>
												) : null}
												{selling_price ? (
													<InfoStack
														icon={<Icons.BankNotes color="muted" />}
														label="Selling Price/Person"
														subtext={
															<Money money={selling_price} showCurrency />
														}
													/>
												) : null}
												{apply_before_days ? (
													<InfoStack
														icon={<Icons.CheckList color="muted" />}
														label="Application Minimum Days"
														subtext={`${apply_before_days} Days`}
													/>
												) : null}
											</Inline>
											<Markdown>{description}</Markdown>
										</Stack>
									</Stack>
									<Divider />
									<Alert
										status="success"
										title="Are you sure you want to restore this Service? Restoring the Service:"
									>
										<Box as="ul" listStyleType="disc" marginLeft="4">
											<li>Will restore this visa service</li>
											<li>
												Will make the service available for selection at the
												time of package creation
											</li>
										</Box>
									</Alert>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Restoring..." : "Restore"}
								</Button>
								<Button onClick={close} disabled={submitting}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function DocumentInfo({ documentGroups }: { documentGroups?: IDocGroup[] }) {
	if (!documentGroups) return null
	return (
		<Stack gap="2">
			<Inline gap="1" alignItems="center">
				<Icons.Document />
				<Text color="muted">Documents List</Text>
			</Inline>
			<Inline gap="2" flexWrap="wrap">
				{documentGroups[0].document_types.map((doc) => (
					<Badge key={doc.id}>
						<Text fontWeight="normal" fontSize="sm">
							{doc.name}
						</Text>
					</Badge>
				))}
			</Inline>
		</Stack>
	)
}

function VisaTypeEditDialog({
	visaService,
	visaType,
	children,
	onSuccess,
}: {
	visaService: TVisa
	visaType: TVisa["visa_types"][number]
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const { name } = visaType
	const [isOpen, open, close] = useDialog()
	const { name: visaName, countries, trip_destinations, currency } = visaService
	const initialValues = {
		id: visaType.id,
		name: visaType.name,
		type_of_visa: getTypeOfVisaOption(visaType.type_of_visa),
		entry_type: getEntryTypeOption(visaType.entry_type),
		purpose: getPurposeOfVisaOption(visaType.purpose),
		day_and_hour: getProcessingTimeInHours(visaType.processing_time_in_hours),
		duration: visaType.duration_iso,
		cost_price:
			visaType.cost_price && typeof visaType.cost_price === "object"
				? formatMoneyByDecimal(visaType.cost_price)
				: undefined,
		selling_price:
			visaType.selling_price && typeof visaType.selling_price === "object"
				? formatMoneyByDecimal(visaType.selling_price)
				: undefined,
		price_valid_till: visaType.price_valid_till,
		apply_before_days: visaType.apply_before_days,
		document_groups: Array.isArray(visaType.document_groups)
			? visaType.document_groups[0]
			: undefined,
		description: visaType.description,
	}
	return (
		<>
			{children({
				open: () => {
					open()
				},
			})}
			<Dialog open={isOpen} onClose={close} xl>
				<Dialog.Header>
					<Dialog.Title paddingLeft="4">Edit {name}</Dialog.Title>
				</Dialog.Header>
				<Form<TVisaTypeForm>
					initialValues={initialValues}
					validate={validateVisaTypeForm}
					onSubmit={withServerErrors(async (values) => {
						if (values.id) {
							await XHR(xhr).updateVisaType(values.id, values)
							showSnackbar(`Visa type "${visaType.name}" updated successfully.`)
							onSuccess?.()
							close()
						} else {
							alert("Visa type ID is missing. Unable to update.")
						}
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body paddingBottom="6">
								<Stack gap="2">
									<Grid>
										<Col xl={4}>
											<Stack gap="1">
												<Inline gap="3" alignItems="center">
													<Heading as="h3" fontSize="lg" fontWeight="semibold">
														Visa Details
													</Heading>
												</Inline>
												<Box>
													<Text color="muted">
														Manage visa service details, destinations, document
														requirements, and pricing information.
													</Text>
												</Box>
											</Stack>
										</Col>
										<Col xl={8}>
											<Inline gap="4" flexWrap="wrap">
												{countries ? (
													<Stack gap="1">
														<Text fontWeight="semibold">Country</Text>

														<Text color="muted" fontSize="base">
															{countries[0].name}
														</Text>
													</Stack>
												) : null}
												{trip_destinations ? (
													<Inline gap="2">
														<Box borderLeftColor="muted" borderWidth="1" />
														<Stack gap="1">
															<Text fontWeight="semibold" wordBreak="all">
																Destination
															</Text>
															<Text color="muted" fontSize="base">
																{trip_destinations[0].name}
															</Text>
														</Stack>
													</Inline>
												) : null}

												{visaName ? (
													<Inline gap="2">
														<Box borderLeftColor="muted" borderWidth="1" />
														<Stack gap="1">
															<Text fontWeight="semibold" wordBreak="all">
																Visa Name
															</Text>
															<Text color="muted" fontSize="base">
																{visaName}
															</Text>
														</Stack>
													</Inline>
												) : null}
												{currency ? (
													<Inline gap="2">
														<Box borderLeftColor="muted" borderWidth="1" />
														<Stack gap="1">
															<Text fontWeight="semibold" wordBreak="all">
																Currency
															</Text>
															<Text color="muted" fontSize="base">
																{currency}
															</Text>
														</Stack>
													</Inline>
												) : null}
											</Inline>
										</Col>
									</Grid>

									<Divider sm />

									<Grid>
										<Col xl={4} paddingX="4">
											<Stack gap="1">
												<Inline gap="3" alignItems="center">
													<Heading as="h3" fontSize="lg" fontWeight="semibold">
														Visa Type
													</Heading>
												</Inline>
												<Box>
													<Text color="muted">
														Add one or more visa types with pricing and
														processing details.
													</Text>
												</Box>
											</Stack>
										</Col>
										<Col xl={8}>
											<Grid noGutters gap="4">
												<Col xl={4} lg={5} md={5} sm={5} xs={12}>
													<Stack gap="1">
														<TextInputField
															label="Name"
															type="text"
															name={`name`}
															placeholder="e.g.- Visa for Tourists (Adults)"
														/>
														<SubText>Provide a descriptive name</SubText>
													</Stack>
												</Col>
												<Col xl={4} lg={2} md={5} sm={5} xs={12}>
													<Stack gap="1">
														<SelectField
															label="Type of Visa"
															name={`type_of_visa`}
															select={Select}
															options={TYPE_OF_VISA_OPTIONS}
															fullWidth
														/>
														<SubText>
															Choose the applicable visa type (e.g., E-visa,
															Transit Visa).
														</SubText>
													</Stack>
												</Col>

												<Col md={3} sm="auto" xs="auto">
													<Stack gap="1">
														<DayAndHourInputField
															label="Processing Day or Hour"
															name={`day_and_hour`}
														/>
														<SubText>
															Enter the expected processing duration in days or
															hours.
														</SubText>
													</Stack>
												</Col>
												<Col xl={3} lg="auto" md="auto" sm="auto" xs="auto">
													<Stack gap="1">
														<DurationInputField
															name={`duration`}
															label="Visa Duration"
														/>
														<SubText>
															Specify how long the visa remains valid.
														</SubText>
													</Stack>
												</Col>
												<Col md={3} sm={5} xs={12}>
													<Stack gap="1">
														<SelectField
															label="Entries"
															name={`entry_type`}
															select={Select}
															fullWidth
															options={ENTRY_TYPE_OPTIONS}
														/>
														<SubText>
															Choose whether the visa allows Single or Multiple
															entries.
														</SubText>
													</Stack>
												</Col>
												<Col md={3} sm={5} xs={12}>
													<Stack gap="1">
														<SelectField
															label="Purpose of Visa"
															name={`purpose`}
															select={Select}
															fullWidth
															options={PURPOSE_OF_VISA_OPTIONS}
														/>
														<SubText>
															Choose the intended travel purpose.
														</SubText>
													</Stack>
												</Col>
												<Col md={3} sm={5} xs={12}>
													<Stack gap="1">
														<GetFieldValue<
															TVisaServiceForm["currency"]
														> name="currency">
															{({ value: currency }) => (
																<TextInputField
																	label="Cost Price/Person"
																	secondaryLabel={currency}
																	type="number"
																	name={`cost_price`}
																	placeholder="0"
																/>
															)}
														</GetFieldValue>
														<SubText>
															Specify the charge per traveler for this visa
															type.
														</SubText>
													</Stack>
												</Col>
												<Col md={3} sm={5} xs={12}>
													<Stack gap="1">
														<GetFieldValue<
															TVisaServiceForm["currency"]
														> name="currency">
															{({ value: currency }) => (
																<TextInputField
																	label="Selling Price/Person"
																	secondaryLabel={currency}
																	type="number"
																	name={`selling_price`}
																	placeholder="0"
																/>
															)}
														</GetFieldValue>
														<SubText>
															Specify the charge per traveler for this visa
															type.
														</SubText>
													</Stack>
												</Col>

												<Col md="auto" xs={12}>
													<Stack gap="1">
														<DatePickerField
															label="Rate Valid Upto"
															name={`price_valid_till`}
															dateFormat
															timeFormat
															required
															min={new Date()}
														/>
														<SubText>
															Specify until when the current rate is valid.
														</SubText>
													</Stack>
												</Col>
												{/* Application Minimum Days */}
												<Col xl={4} lg={4} md={5} sm={5} xs={12}>
													<Stack gap="1">
														<TextInputField
															label="Application Minimum Days"
															secondaryLabel="optional"
															type="number"
															name={`apply_before_days`}
															placeholder="0"
														/>
														<SubText>
															Enter the minimum advance days required before the
															travel date.
														</SubText>
													</Stack>
												</Col>
												<Col xl={12} lg={12} md={12} sm={12} xs={12}>
													<Stack gap="1">
														<GetFieldValue<IDocGroup> name={`document_groups`}>
															{({ value: documentGroup }) => (
																<SelectedDocumentGroup
																	label="Visa Type Documents"
																	editMode
																	name={`document_groups`}
																	documentGroup={documentGroup}
																	addDocumentButtonText="Add Visa Type Documents"
																/>
															)}
														</GetFieldValue>
														<SubText>
															Add the mandatory documents applicants must
															submit.
														</SubText>
													</Stack>
												</Col>
												{/* Description */}
												<Col xl={12} lg={12} md={12} xs={12}>
													<Stack gap="1">
														<MarkdownInputField
															name={`description`}
															label="Description"
															secondaryLabel="optional"
															required
															max={1000}
															placeholder="Enter Notes Here"
															rows={6}
														/>
														<SubText>
															Include any instructions, terms, or remarks
															related to this visa type.
														</SubText>
													</Stack>
												</Col>
											</Grid>
										</Col>
									</Grid>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Grid>
									<Col xl={{ span: 8, offset: 4 }}>
										<Stack gap="4">
											<SubmissionError />
											<Inline gap="4">
												<Button
													type="submit"
													disabled={submitting}
													status="primary"
												>
													{submitting ? "Saving..." : "Save"}
												</Button>
												<Button onClick={close} disabled={submitting}>
													Cancel
												</Button>
											</Inline>
										</Stack>
									</Col>
								</Grid>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

export function SelectedDocumentGroup({
	label = "Documents",
	editMode = false,
	onSuccess,
	name,
	documentGroup,
	addDocumentButtonText = "Add Documents",
}: {
	label?: string
	editMode?: boolean
	onSuccess?: (values: TAddDocumentFormValues) => void
	name?: string
	documentGroup?: IDocGroup
	/**
	 * Optional: Customizes the button text for adding a document to the group.
	 * Defaults to "Add Documents" if not provided.
	 */
	addDocumentButtonText?: string
}) {
	if (editMode && name) {
		return (
			<AddDocumentDialog
				documentGroup={documentGroup}
				name={name}
				onSuccess={(values) => {
					if (values) {
						onSuccess?.(values)
					}
				}}
			>
				{({ open }) => (
					<Stack gap="2">
						{!documentGroup ? (
							<Box>
								<Button status="primary" level="secondary" onClick={open}>
									<Icons.Plus /> {addDocumentButtonText}
								</Button>
							</Box>
						) : (
							<Stack
								borderWidth="1"
								borderColor="muted"
								rounded="md"
								gap="1"
								bgColor="subtle"
							>
								<Inline
									alignItems="center"
									justifyContent="between"
									paddingX="3"
									paddingY="1"
									borderBottomWidth="1"
									borderBottomColor="muted"
								>
									<Text fontWeight="semibold" fontSize="base">
										{label}
									</Text>
									{editMode ? (
										<Button level="secondary" onClick={open}>
											<Icons.Pencil color="muted" />
										</Button>
									) : null}
								</Inline>
								<Stack paddingX="3" paddingY="1" gap="1">
									<Text fontWeight="semibold" fontSize="base">
										{documentGroup.name || "Group Name"}
									</Text>
									<Text color="muted">
										{Array.isArray(documentGroup.document_types) &&
										documentGroup.document_types.length > 0
											? documentGroup.document_types
													.map((doc) => doc.name)
													.join(", ")
											: "Documents"}
									</Text>
								</Stack>
							</Stack>
						)}
						<ErrorMessage name={name} />
					</Stack>
				)}
			</AddDocumentDialog>
		)
	} else if (documentGroup && !editMode) {
		return (
			<Stack
				borderWidth="1"
				borderColor="muted"
				rounded="md"
				gap="1"
				bgColor="subtle"
			>
				<Inline
					alignItems="center"
					justifyContent="between"
					paddingX="3"
					paddingY="1"
					borderBottomWidth="1"
					borderBottomColor="muted"
				>
					<Text fontWeight="semibold" fontSize="base">
						{label}
					</Text>
				</Inline>
				<Stack paddingX="3" paddingY="1" gap="1">
					<Text fontWeight="semibold" fontSize="base">
						{documentGroup.name || "Group Name"}
					</Text>
					<Text color="muted">
						{Array.isArray(documentGroup.document_types) &&
						documentGroup.document_types.length > 0
							? documentGroup.document_types.map((doc) => doc.name).join(", ")
							: "Documents"}
					</Text>
				</Stack>
			</Stack>
		)
	}
}

type AddDocumentTabType = "custom_documents" | "document_groups"

type TAddDocumentFormValues = {
	active_tab: AddDocumentTabType
	selectedGroups: IDocGroup[]
	name: string
	selected_documents: IDocType[]
}

/** Set to true to allow selecting multiple document groups in the UI. */
const ALLOW_MULTIPLE_GROUP_SELECTION = true

function isGroupSelected(
	group: IDocGroup,
	selectedGroups: IDocGroup[] = []
): boolean {
	if (!group?.id) return false
	return selectedGroups?.some((sg) => sg.id === group.id)
}

function toggleGroupSelection(
	currentSelectedGroups: IDocGroup[],
	group: IDocGroup,
	isSelected: boolean
): IDocGroup[] {
	const removeGroup = (groups: IDocGroup[], group: IDocGroup) =>
		groups.filter((csg) => csg.id !== group.id)
	const addGroup = (groups: IDocGroup[], group: IDocGroup) => [
		...(ALLOW_MULTIPLE_GROUP_SELECTION ? groups : []),
		group,
	]

	if (isSelected) {
		return removeGroup(currentSelectedGroups, group)
	} else {
		return addGroup(currentSelectedGroups, group)
	}
}

function getAllFilteredGroupsDocuments(
	selectedGroups: IDocGroup[],
	group: IDocGroup,
	selectedDocuments: IDocType[]
): TAddDocumentFormValues["selected_documents"] {
	const groupsDocuments = selectedGroups.flatMap((sg) => sg.document_types)
	const groupDocuments = group.document_types
	const newSelectedDocuments = selectedDocuments.filter(
		(sd) => !groupDocuments.some((gd) => gd.id === sd.id)
	)
	const allDocs = [
		...(ALLOW_MULTIPLE_GROUP_SELECTION ? newSelectedDocuments : []),
		...groupsDocuments,
	]
	const mergedDocuments = allDocs.reduce<IDocType[]>((acc, doc) => {
		if (!acc.some((d) => d.id === doc.id)) {
			acc.push(doc)
		}
		return acc
	}, [])
	return mergedDocuments
}

function handleGroupSelection({
	form,
	group,
	selectedGroups,
	selectedDocuments,
	isActive,
}: {
	form: FormApi<TAddDocumentFormValues, Partial<TAddDocumentFormValues>>
	group: IDocGroup
	selectedGroups: IDocGroup[]
	selectedDocuments: IDocType[]
	isActive: boolean
}) {
	const updatedGroups = toggleGroupSelection(selectedGroups, group, isActive)
	const updatedDocuments = getAllFilteredGroupsDocuments(
		updatedGroups,
		group,
		selectedDocuments
	)
	const groupName =
		updatedGroups.length === 1
			? updatedGroups[0].name
			: "Default Document Group"
	form.batch(() => {
		form.change("selectedGroups", updatedGroups)
		form.change("selected_documents", updatedDocuments)
		form.change("name", groupName)
	})
}

function getAddDocumentInitialValues(
	documentGroup?: IDocGroup
): TAddDocumentFormValues {
	return {
		active_tab: "document_groups",
		selectedGroups: [],
		name: documentGroup?.name ?? "Default Document Group",
		selected_documents: documentGroup?.document_types ?? [],
	}
}

function toggleDocumentSelection(
	selectedDocuments: IDocType[],
	doc: IDocType,
	checked: boolean
): IDocType[] {
	if (checked) {
		if (selectedDocuments.some((d) => d.id === doc.id)) return selectedDocuments
		return [...selectedDocuments, doc]
	}
	return selectedDocuments.filter((d) => d.id !== doc.id)
}

const validationSchema = validateFormValues(
	Validator.object().shape({
		name: Validator.string()
			.required("Document group name is required")
			.min(2, "Document group name must be greater than two character")
			.max(500, "Document group name must be less than 500 characters"),
		selected_documents: Validator.array()
			.of(Validator.mixed<IDocType>())
			.required("Please select at least one document.")
			.min(1, "Please select at least one document."),
	})
)

function AddDocumentDialog({
	children,
	name,
	onSuccess,
	documentGroup,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	name?: string
	onSuccess?: (data?: TAddDocumentFormValues) => void
	documentGroup?: IDocGroup
}) {
	const [isOpen, open, close] = useDialog()
	const formKeyRef = useRef(0)
	const xhr = useXHR()

	const {
		data: groupsResponse,
		isLoading,
		mutate,
	} = useSWR<IListResponse<IDocGroup>>(`/document-groups/`, () =>
		DocGroupXHR(xhr).get()
	)
	const groups = groupsResponse?.data ?? []

	const handleOpen = () => {
		formKeyRef.current += 1
		open()
	}
	const form = useForm()

	return (
		<>
			{children({ open: handleOpen })}
			<Dialog open={isOpen} onClose={close} lg title="Add Document">
				<Form<TAddDocumentFormValues>
					key={formKeyRef.current}
					validate={validationSchema}
					initialValues={getAddDocumentInitialValues(documentGroup)}
					onSubmit={withServerErrors(async (values) => {
						const data: IDocGroup = {
							name:
								values.name && values.name.length > 1
									? values.name
									: "Default Document Group",
							document_types: values?.selected_documents ?? [],
						}
						if (values.active_tab === "custom_documents") {
							await DocGroupXHR(xhr).create(data)
							mutate()
						}
						if (name) {
							form.change(name, data)
						}
						onSuccess?.(values)
						close()
					})}
					subscription={{ submitting: true }}
					mutators={{ ...arrayMutators }}
				>
					{({ handleSubmit, submitting }) => {
						return (
							<form noValidate onSubmit={handleSubmit}>
								<Dialog.Body>
									<Stack gap="2">
										<DocumentTabsContent
											groups={groups}
											isLoading={isLoading}
										/>
										<SubmissionError />
									</Stack>
								</Dialog.Body>
								<Dialog.Footer>
									<Inline gap="3" justifyContent="start" width="full">
										<Button
											type="submit"
											status="primary"
											disabled={submitting}
										>
											{submitting ? "Adding..." : "Add"}
										</Button>
										<Button type="button" onClick={close} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
								</Dialog.Footer>
							</form>
						)
					}}
				</Form>
			</Dialog>
		</>
	)
}

function DocumentTabsContent({
	groups,
	isLoading,
}: {
	groups: IDocGroup[]
	isLoading: boolean
}) {
	const form = useForm<TAddDocumentFormValues>()
	const { value: activeTab = "document_groups" } =
		useFieldValue<TAddDocumentFormValues["active_tab"]>("active_tab")

	return (
		<Tabs layout="col" gap="8">
			<TabsList>
				<TabItem
					active={activeTab === "document_groups"}
					id="document_groups"
					onClick={() => {
						if (activeTab !== "document_groups") {
							form.batch(() => {
								form.change("active_tab", "document_groups")
								form.change("selectedGroups", [])
								form.change("selected_documents", [])
								form.change("name", "Default Document Group")
							})
						}
					}}
				>
					<Icons.HorizontalSliders /> Document Groups
				</TabItem>
				<TabItem
					active={activeTab === "custom_documents"}
					id="custom_documents"
					onClick={() => {
						if (activeTab !== "custom_documents") {
							form.batch(() => {
								form.change("active_tab", "custom_documents")
								form.change("selectedGroups", [])
								form.change("selected_documents", [])
								form.change("name", "")
							})
						}
					}}
				>
					<Icons.Columns /> Custom Documents
				</TabItem>
			</TabsList>
			<TabContent>
				<Stack gap="8">
					{activeTab === "document_groups" ? (
						<GroupsList loading={isLoading} groups={groups} />
					) : null}
					<DocumentFieldInput name="selected_documents" />

					{activeTab === "custom_documents" ? (
						<TextInputField
							label="Group Name"
							name="name"
							placeholder="Enter group name"
							required={activeTab === "custom_documents"}
						/>
					) : null}
				</Stack>
			</TabContent>
		</Tabs>
	)
}

function GroupsList({
	groups,
	loading,
}: {
	loading: boolean
	groups: IDocGroup[]
}) {
	return (
		<Box
			position="relative"
			padding="3"
			paddingTop="6"
			borderRadius="md"
			borderWidth="1"
			borderColor="muted"
		>
			<Inline
				position="absolute"
				borderRadius="lg"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="primary"
				paddingX="2"
				paddingY="1"
				style={{ top: "-15px" }}
			>
				<Text fontWeight="semibold">{`Saved Groups (${groups.length})`}</Text>
			</Inline>
			<DocumentGroupsState loading={loading} groups={groups} />
		</Box>
	)
}

function DocumentGroupsState({
	loading,
	groups,
}: {
	loading: boolean
	groups: IDocGroup[]
}) {
	const form = useForm<TAddDocumentFormValues>()
	const { value: selectedGroups } =
		useFieldValue<TAddDocumentFormValues["selectedGroups"]>("selectedGroups")
	const { value: selectedDocuments } =
		useFieldValue<TAddDocumentFormValues["selected_documents"]>(
			"selected_documents"
		)

	if (loading) {
		return (
			<Stack alignItems="center" paddingY="4">
				<Spinner />
			</Stack>
		)
	} else if (groups.length === 0) {
		return (
			<Stack alignItems="center" paddingBottom="2">
				<Icons.Info size="6" color="muted" />
				<Text color="muted" fontWeight="semibold">
					No presets available
				</Text>
			</Stack>
		)
	} else {
		return (
			<HorizontalScrollShadow>
				<Inline gap="4" flexWrap="nowrap">
					{groups.map((group) => {
						if (!group.id) return null
						const groupId = group.id
						const isActive = isGroupSelected(group, selectedGroups)

						const documentString = group.document_types
							.map((doc) => doc.name)
							.join(", ")
						const nameNeedsTruncation = (group.name ?? "").length > 50
						const documentNeedsTruncation = documentString.length > 50
						return (
							<Box
								key={groupId}
								paddingY="2"
								paddingX="3"
								rounded="md"
								borderWidth="1"
								minWidth="max"
								role="button"
								cursor="pointer"
								onClick={() => {
									handleGroupSelection({
										form,
										group,
										isActive,
										selectedDocuments,
										selectedGroups,
									})
								}}
								{...(isActive
									? {
											borderColor: "primary",
											backgroundColor: "primary",
										}
									: {
											borderColor: "default",
											backgroundColor: "default",
										})}
							>
								<Stack gap="1">
									<Inline gap="1" alignItems="center">
										{nameNeedsTruncation ? (
											<Tooltip content={group.name} placement="top-start">
												<Text
													fontWeight="semibold"
													textOverflow="truncate"
													style={{ maxWidth: "200px" }}
												>
													{`${(group.name ?? "").substring(0, 50)}...`}
												</Text>
											</Tooltip>
										) : (
											<Text fontWeight="semibold">{group.name}</Text>
										)}
									</Inline>
									{documentNeedsTruncation ? (
										<Tooltip
											content={
												<Stack gap="1">
													<Inline
														alignItems="center"
														borderBottomWidth="1"
														borderBottomColor="default"
														style={{ width: "fit-content" }}
													>
														<Icons.Document />
														<Text fontWeight="semibold"> Documents:</Text>
													</Inline>
													<Text>{documentString}</Text>
												</Stack>
											}
											placement="top-start"
										>
											<Text
												fontSize="xs"
												color="muted"
												textOverflow="truncate"
												style={{ maxWidth: "200px" }}
											>
												{`${documentString.substring(0, 50)}...`}
											</Text>
										</Tooltip>
									) : (
										<Text fontSize="xs" color="muted">
											{documentString}
										</Text>
									)}
									{group.created_at ? (
										<Text fontSize="xs" color="muted">
											Created on <Time timestamp={group.created_at} />
											{group.created_by?.name
												? ` by ${group.created_by.name}`
												: null}
										</Text>
									) : null}
								</Stack>
							</Box>
						)
					})}
				</Inline>
			</HorizontalScrollShadow>
		)
	}
}

function chunk<T>(arr: T[], size: number): T[][] {
	const out: T[][] = []
	for (let i = 0; i < arr.length; i += size) {
		out.push(arr.slice(i, i + size))
	}
	return out
}

export function ColumnGrid({
	items,
	selectedDocuments,
	onDocumentsChange,
}: {
	items: IDocType[]
	selectedDocuments: IDocType[]
	onDocumentsChange: (documents: IDocType[]) => void
}) {
	const groups = chunk(items, 15)
	const selectedIds = new Set(selectedDocuments.map((d) => d.id))
	return (
		<HorizontalScrollShadow>
			<Inline
				alignItems="stretch"
				gap="2"
				paddingX="4"
				paddingTop="6"
				paddingBottom="8"
			>
				{groups.map((group, i) => (
					<Inline key={i}>
						<Stack as="ul" gap="2" style={{ width: "max-content" }}>
							{group.map((item) => (
								<Box
									as="li"
									key={item.id}
									display="flex"
									alignItems="center"
									gap="2"
								>
									<CheckboxInput
										checked={selectedIds.has(item.id)}
										onChange={(e) => {
											onDocumentsChange(
												toggleDocumentSelection(
													selectedDocuments,
													item,
													e.currentTarget.checked
												)
											)
										}}
									/>
									<Text fontWeight="semibold" fontSize="base">
										{item.name}
									</Text>
								</Box>
							))}
						</Stack>
						<Box borderRightColor="muted" borderRightWidth="2" marginX="6" />
					</Inline>
				))}
			</Inline>
		</HorizontalScrollShadow>
	)
}

export function VisaServiceForm({
	onSubmit,
	onCancel,
	initialValues,
}: Readonly<{
	onSubmit: (data: TVisaServiceForm) => Promise<void>
	onCancel?: () => void
	initialValues?: TVisaServiceForm
}>) {
	return (
		<Form<TVisaServiceForm>
			initialValues={initialValues}
			validate={validateVisaForm}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting }) => {
				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack rounded="lg" gap="2">
							<VisaService />
							<Divider marginY="6" />
							<VisaTypes />
							<Divider sm />
							<Grid gap="4" paddingX="4">
								<Col>
									<Stack gap="4">
										<SubmissionError />
										<Inline gap="4">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Saving..." : "Save Service"}
											</Button>
											{onCancel ? (
												<Button
													onClick={onCancel}
													level="tertiary"
													disabled={submitting}
												>
													Cancel
												</Button>
											) : null}
										</Inline>
									</Stack>
								</Col>
							</Grid>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

function VisaService() {
	return (
		<Stack gap="4">
			<Stack paddingX="4">
				<Inline gap="2" justifyContent="start" alignItems="center">
					<Box
						padding="2"
						bgColor="primary"
						rounded="lg"
						style={{ height: "fit-content" }}
					>
						<Icons.Globe size="6" color="primary" />
					</Box>
					<Stack>
						<Heading as="h4" fontSize="lg">
							Visa Details
						</Heading>
						<Text color="muted">
							Enter the basic information to create and manage a visa service.
						</Text>
					</Stack>
				</Inline>
			</Stack>
			<Divider marginY="0" />
			<Grid>
				<Col xl={2} lg={2} md={5} sm={5} xs={12}>
					<Stack gap="1">
						<SelectField
							label="Country"
							name="countries"
							select={SelectWorldCountries}
							fullWidth
							multiple
						/>
						<SubText>Select the country for which this visa applies</SubText>
					</Stack>
				</Col>

				<Col xl={4} lg={3} md={5} sm={5} xs={12}>
					<Stack gap="1">
						<SelectField
							label="Destinations"
							name="destinations"
							select={SelectTripDestination}
							multiple
							placeholder="Select destinations..."
							fetchOnMount
						/>
						<SubText>
							Select the destinations where this visa is applicable.
						</SubText>
					</Stack>
				</Col>

				<Col xl={4} lg={4} md={5} sm={5} xs={12}>
					<Stack gap="1">
						<TextInputField
							label="Visa Name"
							type="text"
							name="name"
							placeholder="ex-Australia Tourist Visa"
						/>
						<SubText>
							Provide a descriptive visa name for easy identification.
						</SubText>
					</Stack>
				</Col>
				<Col xl={2} lg={2} md={5} sm={5} xs={12}>
					<Stack gap="1">
						<SelectTenantCurrencyInputField name="currency" label="Currency" />
						<SubText>Choose the default currency for this visa service</SubText>
					</Stack>
				</Col>
			</Grid>
			<Grid noGutters gap="4" paddingX="4">
				<Col xl={12} lg={12} md={12} sm={12} xs={12}>
					<Stack gap="1">
						<GetFieldValue<IDocGroup> name={`document_groups`}>
							{({ value: documentGroup }) => (
								<SelectedDocumentGroup
									label="Documents"
									editMode
									name={`document_groups`}
									documentGroup={documentGroup}
								/>
							)}
						</GetFieldValue>
						<SubText>
							Add all mandatory documents applicants need to submit.
						</SubText>
					</Stack>
				</Col>
			</Grid>
		</Stack>
	)
}

function VisaTypes() {
	const { value: defaultDocumentGroup } =
		useFieldValue<TVisaTypeForm["document_groups"]>("document_groups")
	return (
		<Stack gap="2" paddingX="4">
			<Stack>
				<Inline gap="2" justifyContent="start" alignItems="center">
					<Box
						padding="2"
						bgColor="primary"
						rounded="lg"
						style={{ height: "fit-content" }}
					>
						<Icons.GlobeWithBottomLine size="6" color="primary" />
					</Box>
					<Stack>
						<Heading as="h4" fontSize="lg">
							Visa Types
						</Heading>
						<Text color="muted">
							Add one or more visa types with pricing and processing details.
						</Text>
					</Stack>
				</Inline>
				<Divider sm />
			</Stack>
			<FieldArray<TVisaTypeForm> name="visa_types">
				{({ fields: visaTypes }) => {
					const showVisaTypeIndex = (visaTypes.length ?? 0) > 1
					return (
						<Stack gap="4">
							<Stack>
								{visaTypes.map((name, index) => (
									<Stack key={name}>
										<Inline
											key={name}
											style={{ borderBottomWidth: "1.5px" }}
											gap="4"
										>
											{showVisaTypeIndex ? (
												<Stack
													bgColor="subtle"
													paddingY="2"
													style={{
														borderWidth: "1.5px",
														borderTopWidth: index > 0 ? "0px" : "1.5px",
														borderBottomWidth: "0px",
														minWidth: "36px",
														width: "36px",
														maxWidth: "36px",
													}}
													borderBottomWidth="0"
													borderColor="muted"
												>
													<Text
														fontSize="md"
														fontWeight="semibold"
														textAlign="center"
														wordBreak="all"
													>
														{index + 1}
													</Text>
												</Stack>
											) : null}
											<Stack gap="3" paddingY="6">
												{/* Name, Type of visa, Processing Day or Hour */}
												<Grid noGutters gap="4">
													<Col xl={4} lg={5} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<TextInputField
																label="Name"
																type="text"
																name={`${name}.name`}
																placeholder="e.g.- Visa for Tourists (Adults)"
															/>
															<SubText>Provide a descriptive name</SubText>
														</Stack>
													</Col>
													<Col xl={4} lg={2} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<SelectField
																label="Type of Visa"
																name={`${name}.type_of_visa`}
																select={Select}
																options={TYPE_OF_VISA_OPTIONS}
																fullWidth
															/>
															<SubText>
																Choose the applicable visa type (e.g., E-visa,
																Transit Visa).
															</SubText>
														</Stack>
													</Col>

													<Col xl={3} lg={4} md="auto" sm="auto" xs="auto">
														<Stack gap="1">
															<DayAndHourInputField
																label="Processing Day or Hour"
																name={`${name}.day_and_hour`}
															/>
															<SubText>
																Enter the expected processing duration in days
																or hours.
															</SubText>
														</Stack>
													</Col>
													<Col xl={2} lg="auto" md="auto" sm="auto" xs="auto">
														<Stack gap="1">
															<DurationInputField
																name={`${name}.duration`}
																label="Visa Duration"
															/>
															<SubText>
																Specify how long the visa remains valid.
															</SubText>
														</Stack>
													</Col>
													<Col xl={2} lg={2} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<SelectField
																label="Entries"
																name={`${name}.entry_type`}
																select={Select}
																fullWidth
																options={ENTRY_TYPE_OPTIONS}
															/>
															<SubText>
																Choose whether the visa allows Single or
																Multiple entries.
															</SubText>
														</Stack>
													</Col>
													<Col xl={3} lg={2} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<SelectField
																label="Purpose of Visa"
																name={`${name}.purpose`}
																select={Select}
																fullWidth
																options={PURPOSE_OF_VISA_OPTIONS}
															/>
															<SubText>
																Choose the intended travel purpose.
															</SubText>
														</Stack>
													</Col>
													<Col xl={2} lg={2} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<GetFieldValue<
																TVisaServiceForm["currency"]
															> name="currency">
																{({ value: currency }) => (
																	<TextInputField
																		label="Cost Price/Person"
																		secondaryLabel={currency}
																		type="number"
																		name={`${name}.cost_price`}
																		placeholder="0"
																	/>
																)}
															</GetFieldValue>
															<SubText>
																Specify the cost per person for this visa type.
															</SubText>
														</Stack>
													</Col>
													<Col xl={2} lg={2} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<GetFieldValue<
																TVisaServiceForm["currency"]
															> name="currency">
																{({ value: currency }) => (
																	<TextInputField
																		label="Selling Price/Person"
																		secondaryLabel={currency}
																		type="number"
																		name={`${name}.selling_price`}
																		placeholder="0"
																	/>
																)}
															</GetFieldValue>
															<SubText>
																Specify the selling per person for this visa
																type.
															</SubText>
														</Stack>
													</Col>

													<Col xl={2} lg={2} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<DatePickerField
																label="Rate Valid Upto"
																name={`${name}.price_valid_till`}
																dateFormat
																timeFormat
																required
																min={new Date()}
															/>
															<SubText>
																Specify until when the current rate is valid.
															</SubText>
														</Stack>
													</Col>
													{/* Application Minimum Days */}
													<Col xl={4} lg={4} md={5} sm={5} xs={12}>
														<Stack gap="1">
															<TextInputField
																label="Application Minimum Days"
																secondaryLabel="optional"
																type="number"
																name={`${name}.apply_before_days`}
																placeholder="0"
															/>
															<SubText>
																Enter the minimum advance days required before
																the travel date.
															</SubText>
														</Stack>
													</Col>
													<Col xl={12} lg={12} md={12} sm={12} xs={12}>
														<Stack gap="1">
															<GetFieldValue<IDocGroup>
																name={`${name}.document_groups`}
															>
																{({ value: documentGroup }) => (
																	<SelectedDocumentGroup
																		label="Visa Type Documents"
																		editMode
																		name={`${name}.document_groups`}
																		documentGroup={documentGroup}
																		addDocumentButtonText="Add Visa Type Documents"
																	/>
																)}
															</GetFieldValue>
															<SubText>
																Add the mandatory documents applicants must
																submit.
															</SubText>
														</Stack>
													</Col>
													{/* Description */}
													<Col xl={12} lg={12} md={12} xs={12}>
														<Stack gap="1">
															<MarkdownInputField
																name={`${name}.description`}
																label="Description"
																secondaryLabel="optional"
																required
																max={1000}
																placeholder="Enter Notes Here"
																rows={6}
															/>
															<SubText>
																Include any instructions, terms, or remarks
																related to this visa type.
															</SubText>
														</Stack>
													</Col>
												</Grid>
												<Inline justifyContent="end" paddingX="4" gap="2">
													<GetFieldValue<TVisaTypeForm> name={name}>
														{({ value: visaType }) => (
															<Button
																inline
																onClick={() =>
																	visaTypes.push({ ...visaType, id: undefined })
																}
															>
																<Icons.Duplicate /> Duplicate
															</Button>
														)}
													</GetFieldValue>
													<Button
														inline
														onClick={() => {
															visaTypes.remove(index)
														}}
													>
														<Icons.Cancel /> Remove
													</Button>
												</Inline>
											</Stack>
										</Inline>
									</Stack>
								))}
							</Stack>
							<Box>
								<Button
									status="primary"
									level="secondary"
									onClick={() => {
										visaTypes.push({
											name: undefined,
											type_of_visa: undefined,
											entry_type: undefined,
											purpose: undefined,
											day_and_hour: undefined,
											duration: undefined,
											cost_price: undefined,
											selling_price: undefined,
											price_valid_till: undefined,
											apply_before_days: undefined,
											document_groups: defaultDocumentGroup ?? undefined,
											description: undefined,
										})
									}}
								>
									<Icons.Plus /> Add More Type
								</Button>
							</Box>
						</Stack>
					)
				}}
			</FieldArray>
		</Stack>
	)
}

function SubText({ children }: { children: string }) {
	return (
		<Text fontSize="xs" color="muted">
			{children}
		</Text>
	)
}

function ReArrangeVisaTypesDialog({
	service,
	children,
	onSuccess,
}: {
	service: TVisa
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess: () => void
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({
				open: () => {
					open()
				},
			})}
			<Dialog open={isOpen} onClose={close} md title="Visa Re-arrangement">
				<Dialog.Body>
					<VisaTypesRearrangement
						onCancel={close}
						service={service}
						onSuccess={onSuccess}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function VisaTypesRearrangement({
	service,
	onCancel,
	onSuccess,
}: {
	service: TVisa
	onCancel: () => void
	onSuccess: () => void
}) {
	const [data, setData] = useState<TVisa["visa_types"]>(() =>
		service.visa_types.filter((vt) => !vt.deleted_at)
	)
	const [isReordered, setIsReordered] = useState<boolean>(false)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const xhr = useXHR()
	const {
		handleDragStart,
		handleDragOver,
		handleDrop,
		handleDragLeave,
		getRowStyle,
	} = useDragDrop<TVisa["visa_types"][number]>(data, (ids) => {
		setData(ids)
		if (!isReordered) {
			setIsReordered(true)
		}
	})
	async function handleSubmit() {
		try {
			setIsLoading(true)
			const priorityData = data.map((s) => s.id)
			await xhr
				.post(`/visa-services/${service.id}/reorder-visa-types`, {
					ordered_visa_type_ids: priorityData,
				})
				.then((res) => {
					showSnackbar(
						res.data.data.message || "Visa types reordered successfully"
					)
					return res
				})
			onSuccess()
			if (onCancel) {
				onCancel()
			}
		} catch (err) {
			const error = err as Error
			window.alert(
				error?.message ||
					"Sorry! Something went wrong. Please try again after some time."
			)
			setData(service.visa_types)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Stack as="ul" gap="4">
			<Stack gap="2">
				{data.map((s, index) => {
					return (
						<Stack
							key={s.id}
							draggable
							onDragStart={(e) => handleDragStart(e, index)}
							onDragOver={(e) => handleDragOver(e, index)}
							onDrop={(e) => handleDrop(e, index)}
							onDragLeave={handleDragLeave}
							style={getRowStyle(index)}
							padding="1"
							bgColor="default"
							borderColor="default"
							as="li"
							borderWidth="1"
							rounded="md"
							paddingY="2"
						>
							<SingleVisaTypeService service={s} index={index} />
						</Stack>
					)
				})}
			</Stack>
			<Divider sm />
			<Inline justifyContent={"end"} gap={"3"}>
				<Button onClick={onCancel}>Cancel</Button>
				<Button
					level="primary"
					disabled={isLoading || !isReordered}
					status="primary"
					onClick={handleSubmit}
					type="submit"
				>
					{isLoading ? "Submitting..." : "Submit"}
				</Button>
			</Inline>
		</Stack>
	)
}

function SingleVisaTypeService({
	service,
	index,
}: {
	service: TVisa["visa_types"][number]
	index: number
}) {
	const { name, type_of_visa } = service
	return (
		<Inline alignItems="center" justifyContent="between" paddingX="2">
			<Inline gap="4" alignItems="center">
				<Text>{index + 1}</Text>
				<Stack>
					<Text as="h5" fontWeight="semibold" fontSize="md">
						{name}
					</Text>
					<Text as="h6" fontWeight="semibold" color="muted">
						{type_of_visa}
					</Text>
				</Stack>
			</Inline>
			<Box
				padding={"1"}
				bgColor={"accent"}
				color={"primary"}
				borderWidth={"1"}
				borderColor={"accent"}
				rounded="md"
			>
				<Icons.GripVertical />
			</Box>
		</Inline>
	)
}
